/**
 * EINE Unterhaltung je Meldung (Dach #43, Scheibe 1): Erfassungs-Dialog,
 * Meldungs-Chat und Verlauf verschmelzen zu einem chronologischen Strang.
 *
 * Drei Zeilenarten:
 *  - Nachrichten (Melder/Entwicklung) als Blasen — beschriftet aus der
 *    Perspektive des Betrachters („an die Entwicklung" / „an den Melder")
 *  - Ereignisse (Statuswechsel, Übergabe ans Issue, Abschluss) als schmale
 *    Zwischenzeilen, damit der Takt sichtbar bleibt ohne den Lesefluss zu
 *    stören
 *  - der Erfassungs-Dialog als eingeklappter Anfang des Strangs
 *
 * Die Sektion ist zuklappbar; zugeklappt zeigt die Kopfzeile, wie viele
 * Nachrichten seit dem letzten Ansehen dazukamen (localStorage-Lesestand
 * wie bisher, F39 — der serverseitige Lesestand kommt mit Scheibe 2).
 */

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Camera, ChevronRight, Lock, Send, Sparkles, Upload, X } from "lucide-react"
import { toast } from "sonner"
import { STATUS_LABEL } from "@/lib/labels"
import { api, apiError } from "@/services/api"
import { dateiAlsBlob, sitzungsCaptureFalls, tabVerbindenUndAufnehmen } from "@/lib/screenshot"
import { ScreenshotBild } from "@/components/erfassung/ScreenshotBild"
import { NachrichtText } from "@/components/meldung/NachrichtText"
import type {
  MeldungKommentar,
  ReportDetail,
  RueckfrageEntwurf,
  Screenshot,
  StatusHistoryEintrag,
} from "@/types"

/** Ereigniszeile aus einem Statuswechsel — erzählt, nicht protokolliert. */
function ereignisText(v: StatusHistoryEintrag, report: ReportDetail): string {
  const b = v.begruendung ?? ""
  if (v.vonStatus === "entwurf" && v.nachStatus === "neu") return "Meldung abgesendet"
  if (v.nachStatus === "gesichtet") return "Von der Entwicklung gesichtet"
  // testerwerk#57: aus einem bestehenden Issue übernommen
  const uebernommen = /^Aus Forgejo-Issue #(\d+) übernommen/.exec(b)
  if (uebernommen) return `Von netz-fabrik übernommen (Issue #${uebernommen[1]})`
  const issue = /Forgejo-Issue #(\d+)/.exec(b)
  if (issue && v.nachStatus === "in_umsetzung" && !b.includes("reopened")) {
    return `An die Entwicklung übergeben (Issue #${issue[1]})`
  }
  if (b.includes("closed") || v.nachStatus === "erledigt") {
    return "Die Entwicklung hat die Umsetzung abgeschlossen — erledigt"
  }
  if (b.includes("reopened")) return "Wieder geöffnet — die Meldung ist zurück bei der Entwicklung"
  const wechsel = `${v.vonStatus ? `${STATUS_LABEL[v.vonStatus]} → ` : ""}${STATUS_LABEL[v.nachStatus]}`
  void report
  return b ? `${wechsel} (${b})` : wechsel
}

/** Ein Strang-Element: Nachricht oder Ereignis, gemeinsam sortierbar. */
type StrangEintrag =
  | { art: "nachricht"; zeit: string; nachricht: MeldungKommentar }
  | { art: "ereignis"; zeit: string; text: string }

interface Props {
  report: ReportDetail
  istReviewer: boolean
}

export function Unterhaltung({ report, istReviewer }: Props) {
  const queryClient = useQueryClient()
  const [offen, setOffen] = useState(true)
  const [erfassungOffen, setErfassungOffen] = useState(false)
  const [text, setText] = useState("")
  // Nur der Strang scrollt, nie die Seite (Hans 02.10.2026, wie im Erfassungs-Chat)
  const strangRef = useRef<HTMLDivElement>(null)
  const abschnittRef = useRef<HTMLElement>(null)
  const innenRef = useRef<HTMLDivElement>(null)
  // Hans 06.10.2026: Wer eine Meldung öffnet, landet SOFORT in der
  // Unterhaltung — ohne sichtbares Scrollen, bei jedem Öffnen. Im Strang
  // steht die erste NEUE Nachricht oben (Trennlinie „Neue Nachrichten"),
  // sonst das Ende. Maßgeblich ist der Lesestand VOR dem Öffnen, den der
  // Server beim ersten Gelesen-Markieren zurückgibt (nicht der Zähler aus dem
  // Cache, der nach dem Lesen bis zu 30 s veraltet ist). Fenster (von, bis]:
  // was danach geschrieben wird, gilt nicht mehr als „neu beim Öffnen".
  const [neuFenster, setNeuFenster] = useState<{ fuer: number; von: number; bis: number } | null>(
    null
  )
  const ersterLesestandFuer = useRef<number | null>(null)
  const neuAbRef = useRef<HTMLDivElement>(null)
  // Solange der Anker hält, richtet jede Größenänderung (nachladende
  // Screenshots oberhalb, Bilder im Strang, die Trennlinie) Seite und Strang
  // neu aus — sonst rutscht das Ziel weg (Hans: „nicht immer"). Die erste
  // eigene Bewegung (Scrollrad, Touch, Taste, Klick) oder 5 s beenden ihn.
  const ankerAktiv = useRef(false)

  const { data: kommentare } = useQuery({
    queryKey: ["reports", report.id, "kommentare"],
    queryFn: () => api.reports.kommentare.list(report.id),
    refetchInterval: 30_000,
  })

  // Scheibe 2 (#43): Lesestand SERVERSEITIG — gesehen wird nur markiert,
  // wenn der Strang offen ist; zugeklappt soll der Zähler ja stehen bleiben.
  // `gelesenBis` hält die Antwort des Servers, damit der zugeklappte Zähler
  // auch nach dem Öffnen und trotz Poll-Nachschub stimmt.
  const [gelesenBis, setGelesenBis] = useState<number | null>(null)
  const gelesen = useMutation({
    mutationFn: (id: number) => api.reports.kommentare.gelesen(id),
    onSuccess: (r, fuer) => {
      setGelesenBis(r.gelesenBis)
      if (ersterLesestandFuer.current === fuer) return
      ersterLesestandFuer.current = fuer
      // Erstbesuch (noch nie geöffnet): keine Trennlinie, Strang am Ende
      setNeuFenster(
        r.gelesenVorher !== null && r.gelesenBis > r.gelesenVorher
          ? { fuer, von: r.gelesenVorher, bis: r.gelesenBis }
          : null
      )
    },
  })
  useEffect(() => {
    if (offen && kommentare) {
      gelesen.mutate(report.id)
      // Während des Ankers richtet der ResizeObserver aus; danach wie
      // bisher: neue Nachrichten → ans Ende
      if (!ankerAktiv.current) {
        const el = strangRef.current
        if (el) el.scrollTop = el.scrollHeight
      }
    }
    // gelesen bewusst nicht in den Abhängigkeiten: die Mutation-Identität
    // wechselt je Render, ausschlaggebend sind Öffnung + Nachrichtenstand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, kommentare, report.id])

  // Erste neue Nachricht beim Öffnen (im Fenster des alten Lesestands)
  const neuAb = useMemo(() => {
    if (!neuFenster || neuFenster.fuer !== report.id || !kommentare) return null
    const ids = kommentare
      .map((k) => k.id)
      .filter((id) => id > neuFenster.von && id <= neuFenster.bis)
    return ids.length > 0 ? Math.min(...ids) : null
  }, [neuFenster, kommentare, report.id])

  // Anker beim Öffnen: vor dem ersten Zeichnen (LayoutEffect) — der Leser
  // sieht den Seitenanfang gar nicht erst.
  useLayoutEffect(() => {
    const abschnitt = abschnittRef.current
    if (!abschnitt) return
    ankerAktiv.current = true
    const ausrichten = () => {
      if (!ankerAktiv.current) return
      abschnitt.scrollIntoView({ block: "start", behavior: "instant" })
      const strangEl = strangRef.current
      if (strangEl) {
        const linie = neuAbRef.current
        strangEl.scrollTop = linie ? Math.max(0, linie.offsetTop - 8) : strangEl.scrollHeight
      }
    }
    ausrichten()
    const ro = new ResizeObserver(ausrichten)
    ro.observe(document.body)
    if (innenRef.current) ro.observe(innenRef.current)
    const ereignisse = ["wheel", "touchstart", "keydown", "mousedown"] as const
    const beenden = () => {
      ankerAktiv.current = false
      ro.disconnect()
      ereignisse.forEach((e) => window.removeEventListener(e, beenden))
      window.clearTimeout(zeit)
    }
    ereignisse.forEach((e) => window.addEventListener(e, beenden, { passive: true }))
    const zeit = window.setTimeout(beenden, 5000)
    return beenden
  }, [report.id])

  // Vor der ersten Markierung zählt der Server-Wert aus dem Report-Laden,
  // danach alles oberhalb der zuletzt bestätigten Kommentar-ID.
  const neue =
    gelesenBis === null
      ? report.ungeleseneKommentare
      : (kommentare ?? []).filter((k) => k.id > gelesenBis).length

  const strang = useMemo<StrangEintrag[]>(() => {
    const eintraege: StrangEintrag[] = [
      ...report.verlauf.map((v) => ({
        art: "ereignis" as const,
        zeit: v.zeitpunkt,
        text: ereignisText(v, report),
      })),
      ...(kommentare ?? []).map((k) => ({
        art: "nachricht" as const,
        zeit: k.erstelltAm,
        nachricht: k,
      })),
    ]
    return eintraege.sort((a, b) => new Date(a.zeit).getTime() - new Date(b.zeit).getTime())
  }, [report, kommentare])

  // Scheibe 3 (#34): der Reviewer wählt, ob seine Nachricht eine FRAGE ist —
  // nur die setzt beim Melder die Rückfrage-Pille. Kunden senden immer info.
  // testerwerk#122: dritte Art „Bereitgestellt“ (Lösung auf der Testumgebung)
  const [art, setArt] = useState<"info" | "frage" | "bereitgestellt">("info")
  const alsFrage = art === "frage"
  const setAlsFrage = (frage: boolean) => setArt(frage ? "frage" : "info")
  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
    enabled: istReviewer,
  })
  const testLink = anwendungen?.find((a) => a.id === report.applicationId)?.url ?? ""
  const bereitstellen = () => {
    setArt("bereitgestellt")
    if (!text.trim()) {
      setText(
        `Die Lösung steht auf der Testumgebung bereit: ${testLink || "<Link>"}\n` +
          "Bitte prüfen Sie es dort und geben Sie kurz Bescheid — „gelöst“, wenn alles passt."
      )
    }
  }

  // Abschluss-Protokoll (Hans 28.09.): zwei Wörter schließen den Kreis.
  // Melder: „gelöst", sobald die Entwicklung zuletzt geschrieben hat (In
  // Umsetzung). Entwicklung: „erledigt", sobald der Melder „gelöst" meldet —
  // das Wort schließt Issue und Meldung. ⇥ Tab übernimmt den Vorschlag.
  const letzte = useMemo(() => {
    // Systemnachrichten (PR eröffnet/gemergt) sind kein Gesprächsbeitrag
    const sichtbar = (kommentare ?? []).filter((k) => !k.intern && k.autorName !== "System")
    return sichtbar.length ? sichtbar[sichtbar.length - 1] : null
  }, [kommentare])
  const normalisiert = (s: string) => s.toLowerCase().replace(/[^0-9a-zäöüß]/g, "")
  const vorschlag: string | null =
    report.status !== "in_umsetzung"
      ? null
      : istReviewer
        ? letzte?.rolle === "kunde" && normalisiert(letzte.text) === "gelöst"
          ? "erledigt"
          : null
        : letzte?.rolle === "entwicklung"
          ? "gelöst"
          : null
  // Hans 28.09.: Screenshots gehören zur Nachricht — erst aufnehmen/hochladen
  // (warten auf die Nachricht), dann formulieren (Text + Bilder an die KI),
  // dann senden. Wartende Bilder überleben ein Neuladen (aus report.screenshots).
  const [anhaenge, setAnhaenge] = useState<Screenshot[]>(() =>
    report.screenshots.filter((s) => s.zuNachricht)
  )
  // `inhalt` überstimmt das Eingabefeld — für die Schnellantwort per Knopf
  const senden = useMutation({
    mutationFn: (inhalt?: string) =>
      api.reports.kommentare.create(
        report.id,
        (inhalt ?? text).trim(),
        istReviewer ? art : "info",
        anhaenge.map((s) => s.id)
      ),
    onSuccess: () => {
      setText("")
      setAlsFrage(false)
      setAnhaenge([])
      void queryClient.invalidateQueries({ queryKey: ["reports"] })
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const abschicken = () => {
    if (text.trim() && !senden.isPending) senden.mutate(undefined)
  }
  // Hans 09.10.2026: Nach „Lösung bereitgestellt" antwortet der Melder per
  // Knopf — „gelöst" geht sofort raus (Melderfreigabe), „noch nicht gelöst"
  // belegt das Feld vor, damit er sagt, was fehlt.
  const eingabeRef = useRef<HTMLTextAreaElement>(null)
  const schnellAntwort =
    !istReviewer &&
    report.status === "in_umsetzung" &&
    letzte?.rolle === "entwicklung" &&
    letzte.art === "bereitgestellt"
  const nochNichtGeloest = () => {
    setText("Noch nicht gelöst: ")
    eingabeRef.current?.focus()
  }

  // Scheibe 3: KI-Hilfe — Reviewer formuliert Rückfragen (mit Erstbeantworter),
  // Melder bekommt Hilfe beim Antworten. Beides landet als ENTWURF im
  // Eingabefeld; gesendet wird erst nach menschlichem Blick.
  const [kiOffen, setKiOffen] = useState(false)
  const [stichworte, setStichworte] = useState("")
  const [entwurf, setEntwurf] = useState<RueckfrageEntwurf | null>(null)
  // Rohfassung für die KI: das Stichwort-Feld — oder, wenn leer, die im
  // Nachrichtenfeld begonnene Antwort (Hans 28.09.: „die manuell formulierte
  // Antwort mit den Screenshots an die KI").
  const rohtext = () => stichworte.trim() || text.trim()
  const frageEntwerfen = useMutation({
    mutationFn: () =>
      api.reports.rueckfrageEntwurf(
        report.id,
        rohtext(),
        anhaenge.map((s) => s.id)
      ),
    onSuccess: (e) => {
      setEntwurf(e)
      if (!e.schonBeantwortet && e.frageEntwurf) {
        setText(e.frageEntwurf)
        setAlsFrage(true)
      }
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const antwortHelfen = useMutation({
    mutationFn: () =>
      api.reports.antwortHilfe(
        report.id,
        rohtext(),
        anhaenge.map((s) => s.id)
      ),
    onSuccess: (r) => {
      setText(r.antwort)
      setKiOffen(false)
      setStichworte("")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // Hans 28.09.2026: Screenshot direkt aus der Unterhaltung — vom verbundenen
  // Tab oder als Datei. Er wird hochgeladen und WARTET auf die Nachricht
  // (zu_nachricht): keine eigene Nachricht, kein KI-Aufruf. Beim Senden wird
  // er an die Nachricht gebunden und ans Issue gehängt.
  // Verbindung ggf. hier herstellen („wo ist der Schalter?" — nach
  // Neuladen/Direktaufruf gab es nur „Bild hochladen").
  const captureSchluessel = `app:${report.applicationId}`
  const [tabVerbunden, setTabVerbunden] = useState(
    () => sitzungsCaptureFalls(captureSchluessel) !== null
  )
  const [tabVerbindet, setTabVerbindet] = useState(false)
  const dateiInputRef = useRef<HTMLInputElement>(null)
  const screenshotSenden = useMutation({
    mutationFn: (bild: Blob) => api.reports.screenshots.upload(report.id, bild, [], true),
    onSuccess: (screenshot) => {
      setAnhaenge((alt) => [...alt, screenshot])
      void queryClient.invalidateQueries({ queryKey: ["reports", report.id] })
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const anhangEntfernen = useMutation({
    mutationFn: (screenshotId: number) => api.reports.screenshots.remove(report.id, screenshotId),
    onSuccess: (_d, screenshotId) => {
      setAnhaenge((alt) => alt.filter((s) => s.id !== screenshotId))
      void queryClient.invalidateQueries({ queryKey: ["reports", report.id] })
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const vomTabAufnehmen = async () => {
    setTabVerbindet(true)
    try {
      const bild = await tabVerbindenUndAufnehmen(captureSchluessel, !!report.kontext?.ninoxTeamId)
      setTabVerbunden(true)
      screenshotSenden.mutate(bild)
    } catch (e) {
      setTabVerbunden(sitzungsCaptureFalls(captureSchluessel) !== null)
      // Abbruch im Freigabe-Dialog ist kein Fehler
      if ((e as Error).name !== "NotAllowedError") {
        toast.error("Aufnahme nicht möglich — bitte den Anwendungs-Tab erneut freigeben")
      }
    } finally {
      setTabVerbindet(false)
    }
  }
  const screenshotsMoeglich = !["entwurf", "abgelehnt", "verworfen"].includes(report.status)

  // Perspektive des Betrachters (Dach #43): die eigene Seite steht rechts,
  // die Beschriftung nennt die Richtung — nicht die abstrakte Rolle.
  const eigeneSeite = istReviewer ? "entwicklung" : "kunde"
  const kopfzeile = (k: MeldungKommentar): string => {
    const name = k.autorName || (k.rolle === "entwicklung" ? "Entwicklung" : "Melder")
    if (k.intern) return `${name} · intern (Forgejo)`
    // Scheibe 6 (#43): Kunden-Nachricht eines Dritten kennzeichnen
    const dritter = k.rolle === "kunde" && !k.vomMelder ? " (ebenfalls betroffen)" : ""
    if (k.rolle === eigeneSeite) {
      return `${name}${dritter} · an ${istReviewer ? "den Melder" : "die Entwicklung"}`
    }
    return istReviewer
      ? `${name} (${k.vomMelder ? "Melder" : "ebenfalls betroffen"})`
      : `${name} (Entwicklung) · an dich`
  }

  return (
    <section ref={abschnittRef} className="scroll-mt-4 rounded-xl border bg-card p-5 shadow-card">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        className="flex w-full items-center gap-2 text-left"
      >
        <ChevronRight
          className={`h-4 w-4 text-muted-foreground transition-transform ${offen ? "rotate-90" : ""}`}
        />
        <h2 className="text-[13px] font-bold uppercase tracking-wide text-foreground/70">
          Unterhaltung
          {kommentare && kommentare.length > 0 && (
            <span className="ml-1.5 font-semibold text-muted-foreground">
              · {kommentare.length} Nachricht{kommentare.length === 1 ? "" : "en"}
            </span>
          )}
        </h2>
        {!offen && neue > 0 && (
          <span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold text-primary-foreground">
            {neue} neue
          </span>
        )}
        {report.rueckfrageOffen && !istReviewer && (
          <span
            title="Die Entwicklung hat eine Rückfrage gestellt, die noch unbeantwortet ist"
            className="rounded-full bg-warning px-2.5 py-0.5 text-xs font-bold text-warning-foreground"
          >
            Rückfrage offen — bitte antworten
          </span>
        )}
      </button>

      {offen && (
        <div className="mt-3 flex flex-col gap-3">
          {/* Farb-Legende: die Rollenfarben erklären sich selbst */}
          <div className="flex items-center gap-4 text-[11.5px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full border border-amber-400/40 bg-amber-400/30" />
              Melder
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full border border-sky-400/40 bg-sky-400/30" />
              Entwicklung
            </span>
            {istReviewer && (
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full border border-dashed border-sky-400/50" />
                intern (nur Reviewer)
              </span>
            )}
          </div>

          {/* Der Anfang des Strangs: Erfassungs-Dialog, eingeklappt */}
          {report.chat.length > 0 && (
            <div className="rounded-[10px] border">
              <button
                type="button"
                onClick={() => setErfassungOffen((o) => !o)}
                className="flex w-full items-center gap-2 px-3.5 py-2 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
              >
                <ChevronRight
                  className={`h-3.5 w-3.5 transition-transform ${erfassungOffen ? "rotate-90" : ""}`}
                />
                Erfassungs-Dialog · {report.chat.length} Nachrichten
              </button>
              {erfassungOffen && (
                <div className="flex flex-col gap-2 border-t p-3.5">
                  {report.chat.map((n) => (
                    <div
                      key={n.id}
                      className={`max-w-[75%] rounded-[10px] px-3 py-2 text-[13px] ${
                        n.rolle === "kunde"
                          ? "self-end border border-amber-400/25 bg-amber-400/10"
                          : "self-start bg-muted"
                      }`}
                    >
                      <NachrichtText text={n.inhalt} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Der Strang: Nachrichten + Ereignisse, chronologisch */}
          <div ref={strangRef} className="relative max-h-[420px] overflow-y-auto pr-1">
            <div ref={innenRef} className="flex flex-col gap-2">
            {strang.map((e, i) => (
              <Fragment key={e.art === "ereignis" ? `e${i}` : `n${e.nachricht.id}`}>
              {e.art === "nachricht" && e.nachricht.id === neuAb && (
                <div ref={neuAbRef} className="flex items-center gap-2 py-0.5">
                  <span className="h-px flex-1 bg-primary/60" />
                  <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-primary-foreground">
                    Neue Nachrichten
                  </span>
                  <span className="h-px flex-1 bg-primary/60" />
                </div>
              )}
              {e.art === "ereignis" ? (
                <div key={`e${i}`} className="flex items-center gap-2 py-0.5">
                  <span className="h-px flex-1 bg-border" />
                  <span className="shrink-0 text-[11.5px] font-medium text-muted-foreground">
                    {e.text} ·{" "}
                    {new Date(e.zeit).toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              ) : e.nachricht.autorName === "System" ? (
                // Scheibe 4: Systemnachrichten (PR eröffnet/gemergt) lesen
                // sich wie Ereignisse, nicht wie Gesprächsbeiträge
                <div key={`n${e.nachricht.id}`} className="flex items-center gap-2 py-0.5">
                  <span className="h-px flex-1 bg-border" />
                  <span className="shrink-0 text-[11.5px] font-medium text-muted-foreground">
                    {e.nachricht.text} ·{" "}
                    {new Date(e.nachricht.erstelltAm).toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              ) : (
                <div
                  key={`n${e.nachricht.id}`}
                  className={`max-w-[85%] rounded-[10px] px-3.5 py-2.5 text-sm ${
                    // Farbe nach ROLLE (Wunsch 12.08.2026): Melder blassgelb,
                    // Entwicklung hellblau — auf beiden Seiten gleich, damit
                    // Melder und Reviewer dieselben Farben sehen. Die eigene
                    // Seite steht weiterhin rechts. Transluzente Tönungen,
                    // damit es im dunklen Thema trägt.
                    e.nachricht.intern
                      ? "self-start border border-dashed border-sky-400/30 bg-sky-400/5 opacity-90"
                      : `${
                          e.nachricht.rolle === eigeneSeite ? "self-end" : "self-start"
                        } border ${
                          e.nachricht.rolle === "kunde"
                            ? "border-amber-400/25 bg-amber-400/10"
                            : "border-sky-400/25 bg-sky-400/10"
                        }`
                  }`}
                >
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    {kopfzeile(e.nachricht)} ·{" "}
                    {new Date(e.nachricht.erstelltAm).toLocaleString("de-DE")}
                    {e.nachricht.art === "frage" && (
                      <span className="rounded-full bg-warning px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-warning-foreground">
                        Frage
                      </span>
                    )}
                    {e.nachricht.art === "bereitgestellt" && (
                      <span className="rounded-full bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-sky-700 dark:text-sky-300">
                        Lösung bereitgestellt
                      </span>
                    )}
                    {e.nachricht.intern && (
                      <Lock className="h-3 w-3" aria-label="Nur für Reviewer sichtbar" />
                    )}
                  </p>
                  <NachrichtText text={e.nachricht.text} />
                  {e.nachricht.screenshotIds?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {e.nachricht.screenshotIds.map((sid) => {
                        const s = report.screenshots.find((x) => x.id === sid)
                        return s ? (
                          <ScreenshotBild
                            key={sid}
                            reportId={report.id}
                            screenshot={s}
                            className="w-48"
                          />
                        ) : null
                      })}
                    </div>
                  )}
                </div>
              )}
              </Fragment>
            ))}
            {strang.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Noch keine Einträge — Fragen zur Meldung kannst du hier direkt stellen.
              </p>
            )}
            </div>
          </div>

          {/* Hans 28.09.: Screenshot aus dem verbundenen Tab, von der KI beschrieben */}
          {screenshotsMoeglich && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void vomTabAufnehmen()}
                disabled={screenshotSenden.isPending || tabVerbindet}
                className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-[12px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                <Camera className="h-3.5 w-3.5" />
                {screenshotSenden.isPending
                  ? "Screenshot wird gespeichert …"
                  : tabVerbindet
                    ? "Tab wird freigegeben …"
                    : tabVerbunden
                      ? "Screenshot vom verbundenen Tab"
                      : "Tab verbinden & Screenshot"}
              </button>
              <button
                type="button"
                onClick={() => dateiInputRef.current?.click()}
                disabled={screenshotSenden.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 text-[12px] font-bold hover:bg-secondary/80 disabled:opacity-50"
              >
                <Upload className="h-3.5 w-3.5" /> Bild hochladen
              </button>
              <input
                ref={dateiInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={async (e) => {
                  const datei = e.target.files?.[0]
                  e.target.value = ""
                  if (datei) screenshotSenden.mutate(await dateiAlsBlob(datei))
                }}
              />
              <span className="text-[11.5px] text-muted-foreground">
                {tabVerbunden
                  ? "Das Bild hängt an deiner nächsten Nachricht — schreib dazu, und „Formulieren lassen“ nimmt Text und Bild."
                  : "Der Browser fragt einmal, welcher Tab geteilt wird — die Verbindung bleibt für diese Sitzung."}
              </span>
              {anhaenge.length > 0 && (
                <div className="flex w-full flex-wrap gap-2 rounded-[10px] border border-dashed p-2">
                  {anhaenge.map((s) => (
                    <div key={s.id} className="relative w-32">
                      <ScreenshotBild reportId={report.id} screenshot={s} />
                      <button
                        type="button"
                        onClick={() => anhangEntfernen.mutate(s.id)}
                        title="Bild von der Nachricht entfernen"
                        className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-destructive-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <span className="self-center text-[11.5px] text-muted-foreground">
                    {anhaenge.length} Bild{anhaenge.length === 1 ? "" : "er"} an der nächsten
                    Nachricht
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Scheibe 3: KI-Hilfe — Entwurf entsteht hier, gesendet wird unten */}
          {report.status !== "entwurf" && (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setKiOffen((o) => !o)}
                className="flex items-center gap-1.5 self-start rounded-lg px-2 py-1 text-[12px] font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {istReviewer
                  ? "Rückfrage mit KI formulieren"
                  : "Antwort mit KI-Hilfe formulieren"}
              </button>
              {kiOffen && (
                <div className="flex flex-col gap-2 rounded-[10px] border border-primary/20 bg-primary/5 p-3">
                  <textarea
                    value={stichworte}
                    onChange={(e) => setStichworte(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder={
                      istReviewer
                        ? "Rohe Stichworte (oder leer lassen und den Text im Nachrichtenfeld nehmen) — die KI prüft erst die Unterlagen und formuliert dann kundentauglich …"
                        : "Rohe Stichworte (oder leer lassen und deine Antwort im Nachrichtenfeld nehmen) — die KI formuliert daraus, mit den angehängten Bildern …"
                    }
                    className="rounded-lg border bg-background px-3 py-2 text-[13px]"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        istReviewer ? frageEntwerfen.mutate() : antwortHelfen.mutate()
                      }
                      disabled={
                        !rohtext() ||
                        frageEntwerfen.isPending ||
                        antwortHelfen.isPending
                      }
                      className="rounded-lg bg-primary px-3 py-1.5 text-[12px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
                    >
                      {frageEntwerfen.isPending || antwortHelfen.isPending
                        ? "KI arbeitet …"
                        : "Formulieren lassen"}
                    </button>
                    <span className="text-[11.5px] text-muted-foreground">
                      Der Entwurf landet im Nachrichtenfeld — gesendet wird erst von dir.
                    </span>
                  </div>
                  {/* Erstbeantworter: die Unterlagen beantworten die Frage schon */}
                  {istReviewer && entwurf?.schonBeantwortet && (
                    <div className="rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-[13px]">
                      <p className="font-bold text-success">
                        Das beantworten die Unterlagen bereits — der Melder muss nicht
                        gefragt werden:
                      </p>
                      <p className="mt-1 whitespace-pre-wrap">
                        {entwurf.antwortAusUnterlagen}
                      </p>
                      {entwurf.hinweis && (
                        <p className="mt-1 text-muted-foreground">{entwurf.hinweis}</p>
                      )}
                    </div>
                  )}
                  {istReviewer && entwurf && !entwurf.schonBeantwortet && entwurf.hinweis && (
                    <p className="text-[12px] text-muted-foreground">{entwurf.hinweis}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Eingabe — Richtung aus der Perspektive des Betrachters.
              Beim Entwurf zu: der Chat öffnet mit dem Absenden (409 im API). */}
          {report.status === "erledigt" && !istReviewer && (
            <p className="mb-2 text-[12px] text-muted-foreground">
              Diese Meldung ist <strong>erledigt</strong>. Eine Antwort öffnet sie wieder und
              bringt sie zurück zur Entwicklung.
            </p>
          )}
          {schnellAntwort && (
            <div className="mb-2 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
              <span>Passt die Lösung?</span>
              <button
                type="button"
                onClick={() => !senden.isPending && senden.mutate("gelöst")}
                disabled={senden.isPending}
                className="rounded-full bg-success px-3.5 py-1 text-[12.5px] font-bold text-success-foreground hover:opacity-90 disabled:opacity-50"
              >
                Gelöst
              </button>
              <button
                type="button"
                onClick={nochNichtGeloest}
                disabled={senden.isPending}
                className="rounded-full border border-primary/40 bg-card px-3.5 py-1 text-[12.5px] font-semibold text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
              >
                Noch nicht gelöst …
              </button>
            </div>
          )}
          {report.status !== "entwurf" && (
          <div className="flex items-end gap-2">
            {istReviewer && (
              <div
                className="flex shrink-0 flex-col overflow-hidden rounded-lg border text-[11px] font-bold"
                title="Nur eine FRAGE setzt beim Melder die Rückfrage-Pille"
              >
                {(
                  [
                    ["info", "Info"],
                    ["frage", "Frage"],
                    ["bereitgestellt", "Bereit"],
                  ] as const
                ).map(([wert, label]) => (
                  <button
                    key={label}
                    type="button"
                    title={
                      wert === "bereitgestellt"
                        ? "Lösung steht auf der Testumgebung bereit — Text mit Link wird vorbelegt"
                        : undefined
                    }
                    onClick={() => (wert === "bereitgestellt" ? bereitstellen() : setArt(wert))}
                    className={`px-2.5 py-[5px] ${
                      art === wert
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <textarea
              ref={eingabeRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Tab" && !text && vorschlag) {
                  e.preventDefault()
                  setText(vorschlag)
                  return
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  abschicken()
                }
              }}
              rows={2}
              maxLength={4000}
              placeholder={
                vorschlag
                  ? `„${vorschlag}“ — ⇥ Tab übernimmt, Enter sendet${
                      vorschlag === "erledigt" ? " (schließt das Issue)" : ""
                    }`
                  : istReviewer
                  ? art === "bereitgestellt"
                    ? "Lösung bereitgestellt — Link auf die Testumgebung … (Enter sendet)"
                    : alsFrage
                    ? "Rückfrage an den Melder — setzt die Pille … (Enter sendet)"
                    : "Nachricht an den Melder … (Enter sendet)"
                  : "Nachricht an die Entwicklung … (Enter sendet)"
              }
              className="min-h-[44px] flex-1 rounded-lg border px-3 py-2 text-[13px]"
            />
            <button
              type="button"
              onClick={abschicken}
              disabled={!text.trim() || senden.isPending}
              className="flex h-[44px] items-center gap-1.5 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
              Senden
            </button>
          </div>
          )}
        </div>
      )}
    </section>
  )
}
